import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Panel, Spinner, Typography } from "@/components/atoms";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useAddVehicleTypeMutation, useToggleVehicleTypeMutation } from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { addToast } from "@/utils";
import { CategoryPicker } from "./CategoryPicker";
import { DiscardModal } from "./DiscardModal";
import { StepOneFields } from "./StepOneFields";
import { vehicleTypeFormSchema, VehicleTypeFormData } from "./form";

type LocationState = {
  template?: VehicleTypeFormData;
  templateOriginUuid?: string;
  templateOriginName?: string;
};

export const AddVehicleTypePanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [step, setStep] = useState<1 | 2>(1);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [addVehicleType, { isLoading: isAdding }] = useAddVehicleTypeMutation();
  const [toggleVehicleType, { isLoading: isToggling }] = useToggleVehicleTypeMutation();
  const isLoading = isAdding || isToggling;

  const form = useForm<VehicleTypeFormData>({
    resolver: zodResolver(vehicleTypeFormSchema),
    shouldFocusError: true,
    defaultValues: state.template ?? {
      name: "",
      capacity: 0,
      bags: 0,
      waitTimeCost: 0,
      costAdjustment: 0,
      customCommissionEnabled: false,
      customCommissionRate: undefined,
      vehicleCategoryUuid: "",
    },
  });

  const handleNext = async () => {
    const valid = await form.trigger([
      "name",
      "capacity",
      "bags",
      "waitTimeCost",
      "costAdjustment",
      "customCommissionEnabled",
      "customCommissionRate",
    ]);
    if (valid) setStep(2);
  };

  const handleCancel = () => setDiscardOpen(true);

  const handleSave = form.handleSubmit(async (data) => {
    try {
      if (state.templateOriginUuid) {
        try {
          await toggleVehicleType({ id: state.templateOriginUuid, active: false }).unwrap();
        } catch {
          addToast("danger", `Couldn't disable "${state.templateOriginName ?? "the original"}". Toggle it off manually if you like.`);
        }
      }

      await addVehicleType({
        name: data.name,
        is_active: true,
        per_distance_unit_multiplier: data.costAdjustment,
        wait_time_cost: Math.round(data.waitTimeCost * 100),
        vehicle_capacity: { pax: data.capacity, bags: data.bags },
        vehicle_category_uuid: data.vehicleCategoryUuid,
        custom_commission: data.customCommissionEnabled
          ? { enabled: true, rate: data.customCommissionRate ?? 0 }
          : { enabled: false, rate: 0 },
      }).unwrap();

      addToast("success", "Vehicle Type added successfully");
      navigate("./..");
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((m) => addToast("danger", m));
    }
  });

  const isStep1 = step === 1;
  const enteredName = form.watch("name");

  return (
    <FormProvider {...form}>
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col items-center px-5 py-4">
        <Panel className={isStep1 ? "w-full max-w-[550px]" : "w-full max-w-[800px]"}>
          <div className="flex flex-col gap-2">
            <Typography className="text-lg font-semibold leading-5 text-neutral-black">
              {isStep1 ? "Step 1: Add Vehicle Type" : "Step 2: Add Vehicle Type"}
            </Typography>
            <Typography className="text-base leading-5 text-neutral-dark-gray">
              {isStep1 ? (
                "Set the details for this operator vehicle type. These values help define how this operator vehicle type appears and behaves during bookings."
              ) : (
                <>
                  Map your vehicle type: Select the most suitable platform vehicle category for{" "}
                  <span className="text-info">{enteredName?.trim() || "your vehicle"}</span>. This helps RideMinder match your operator
                  vehicle type correctly when you offload jobs.
                </>
              )}
            </Typography>
          </div>
          <div className="mt-6">{isStep1 ? <StepOneFields /> : <CategoryPicker vehicleTypeName={enteredName} />}</div>
          <div className="mt-8 flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            {isStep1 ? (
              <Button variant="primary" onClick={handleNext} endIcon="ArrowRight">
                Next
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setStep(1)} startIcon="ArrowLeft" disabled={isLoading}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isLoading || !form.watch("vehicleCategoryUuid")}
                >
                  {isLoading ? <Spinner /> : "Add Vehicle Type"}
                </Button>
              </>
            )}
          </div>
        </Panel>
      </div>
      <DiscardModal open={discardOpen} onClose={() => setDiscardOpen(false)} onConfirm={() => navigate("./..")} mode="add" />
    </FormProvider>
  );
};
